// ============================================================================
// NOT YET INBOX VIEW - Referrals requiring GP action
//
// Two types of items appear here:
//
//   1. not_yet_outcome   — engine returned NOT_YET (clinical fields missing).
//      Query: outcome.status == 'not_yet' (existing getNotYetReferrals).
//      Action: "Complete This Referral" → Gate rerun.
//
//   2. needs_info_ops    — ROUTINE or URGENT referral that intake set to NEEDS_INFO
//      because documents are missing.
//      Query: workflowStatus == 'NEEDS_INFO' AND outcome.status IN ['routine','urgent']
//      Action: upload requested documents ONLY. No Gate rerun.
//
// INVARIANT: needs_info_ops items must NEVER trigger Gate rerun logic.
//
// FIRESTORE READS:
//   tenants/{tenantId}/referrals  (two separate queries, merged client-side)
//   tenants/{tenantId}/referrals/{referralId}/reworkRequests/{requestId}
// ============================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { safeUserError } from '../core/safeError.js';
import {
  Clock, AlertTriangle, CheckCircle, Loader2, RefreshCw, ChevronRight,
  FileText, Calendar, Package, AlertCircle, Edit3, ChevronDown,
  Upload, CheckCircle2, XCircle, Inbox, CircleDot, Check,
} from 'lucide-react';
import {
  getNotYetReferrals,
  getNeedsInfoReferrals,
  listReworkRequests,
  REWORK_REQUEST_STATUS,
  REWORK_ITEM_TYPE_LABELS,
} from '../core/index.js';
import { getReferral } from '../core/store.referrals.js';
import { findReferralInBuckets, classifyDeepLinkFailure } from '../core/deepLinkReferral.js';
import {
  createAttachmentUploadUrl,
  finalizeAttachmentUpload,
} from '../core/phiAttachmentsApi.js';
// S1 — Never-PHI write hardening: redact PHI values from clinical answers before
// they are persisted to sessionStorage for the Gate resume flow (defends legacy
// pre-S1 referrals too, since this is the sink boundary).
import { redactBlockingPhiDeep } from '../utils/phiScan.js';
import { useAuth } from "../auth/AuthContext.jsx";
import MessagingCard from './Gate/MessagingCard.jsx';
import { RGStatusBadge } from '../components/rg/RGPrimitives.jsx';

const PAGE_SIZE = 10;

// Inbox item types — never change these values; they are internal discriminators.
const INBOX_TYPE = {
  NOT_YET_OUTCOME: 'not_yet_outcome',
  NEEDS_INFO_OPS:  'needs_info_ops',
};

// Map rework item type → Cloud Function attachment type param
const ITEM_TYPE_TO_ATTACHMENT_TYPE = {
  LABS:           'LABS',
  IMAGING_REPORT: 'IMAGING',
  REFERRAL_NOTE:  'PDF',
  INSURANCE:      'PDF',
  MED_LIST:       'PDF',
  OTHER_DOC:      'OTHER',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatRequestDate(ts) {
  if (!ts) return null;
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return null;
  }
}

function tsEpoch(ts) {
  if (!ts) return 0;
  if (ts.seconds) return ts.seconds;
  if (ts.toMillis) return ts.toMillis() / 1000;
  try { return new Date(ts).getTime() / 1000; } catch { return 0; }
}

function isUrgentRow(r) {
  return (r.outcome?.status ?? r.outcomeStatus) === 'urgent';
}

// "Stuck since" epoch — the moment a row started waiting for the next action.
// For Docs Requested (NEEDS_INFO_OPS) rows, the operational stuck-since is
// workflowStatusEnteredAt (when Intake requested docs), NOT the original
// referral creation date. Matches the "Requested" date the card displays.
// For Engine Not Yet rows, the GP has been waiting since the referral was
// created at the Gate — savedAt/createdAt is correct.
function stuckSinceEpoch(r) {
  return r._inboxType === INBOX_TYPE.NEEDS_INFO_OPS
    ? tsEpoch(r.workflowStatusEnteredAt ?? r.savedAt ?? r.createdAt)
    : tsEpoch(r.savedAt ?? r.createdAt);
}

// Inbox ordering: urgent/red-flag items first, then oldest-stuck-first
// (most-stuck at top) within each group — applies to the urgent group too.
function compareInboxRows(a, b) {
  const au = isUrgentRow(a);
  const bu = isUrgentRow(b);
  if (au !== bu) return au ? -1 : 1;                       // urgent group first
  return stuckSinceEpoch(a) - stuckSinceEpoch(b);          // oldest-stuck-first
}

/**
 * Normalize a rework request doc (v1 items[] or v2 flat) into a display entry.
 */
function normalizeRequest(req) {
  if (req.itemType && !Array.isArray(req.items)) {
    const isReceived =
      req.status === REWORK_REQUEST_STATUS.RECEIVED ||
      req.status === REWORK_REQUEST_STATUS.RESOLVED;
    return [{
      key:         req.id,
      requestId:   req.id,
      itemType:    req.itemType,
      isReceived,
      requestedAt: req.createdAt,
      receivedAt:  req.resolvedAt ?? null,
      _epoch:      tsEpoch(req.createdAt),
    }];
  }
  return (req.items ?? []).map((item) => {
    const isReceived = item.itemStatus === 'RECEIVED' || item.itemStatus === 'PROVIDED';
    return {
      key:         `${req.id}__${item.itemType}`,
      requestId:   req.id,
      itemType:    item.itemType,
      isReceived,
      requestedAt: req.createdAt,
      receivedAt:  item.updatedAt ?? req.resolvedAt ?? null,
      _epoch:      tsEpoch(req.createdAt),
    };
  });
}

function flattenAndSort(requests) {
  const open     = [];
  const received = [];
  for (const req of requests) {
    for (const entry of normalizeRequest(req)) {
      if (entry.isReceived) received.push(entry);
      else open.push(entry);
    }
  }
  open.sort((a, b) => a._epoch - b._epoch);
  received.sort((a, b) => a._epoch - b._epoch);
  return [...open, ...received];
}

/**
 * Convert a field ID like "hba1c_result" or "bloodPressure" into "Hba1c Result" / "Blood Pressure".
 */
function formatFieldIdLabel(id) {
  if (!id || typeof id !== 'string') return id;
  // snake_case → space-separated, then capitalize each word
  return id
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Max checklist items shown on the snippet card before "and N more…" */
const SNIPPET_CHECKLIST_MAX = 3;

// ── Requested Missing Items Section ──────────────────────────────────────────

// required=true → always render (even if empty), used for needs_info_ops items.
function RequestedMissingItemsSection({ tenantId, referralId, required = false, onAllResolved }) {
  const [requests, setRequests]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [uploadState, setUploadState] = useState({});
  const [uploadError, setUploadError] = useState({});

  const pendingRef   = useRef(null);
  const fileInputRef = useRef(null);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listReworkRequests(tenantId, referralId);
      setRequests(rows);
      return rows; // returned so caller can check resolution without stale state
    } catch (err) {
      console.error('[NotYetInbox] Failed to load rework requests:', err);
      setError('Could not load requested missing items.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [tenantId, referralId]);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const handleUploadClick = (key, requestId, itemType) => {
    pendingRef.current = { key, requestId, itemType };
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileSelected = async (e) => {
    const file    = e.target.files?.[0];
    const pending = pendingRef.current;
    if (!file || !pending) return;

    const { key, requestId, itemType } = pending;
    const attachmentType = ITEM_TYPE_TO_ATTACHMENT_TYPE[itemType] ?? 'OTHER';

    setUploadState((s) => ({ ...s, [key]: 'uploading' }));
    setUploadError((s) => ({ ...s, [key]: null }));

    try {
      const { attachmentId, uploadUrl } = await createAttachmentUploadUrl(referralId, {
        mimeType:        file.type || 'application/octet-stream',
        originalName:    file.name,
        type:            attachmentType,
        requestId:       requestId,
        requestItemType: itemType,
      });

      const putRes = await fetch(uploadUrl, {
        method:  'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body:    file,
      });
      if (!putRes.ok) {
        throw new Error(`Storage upload failed (HTTP ${putRes.status})`);
      }

      await finalizeAttachmentUpload(referralId, attachmentId);
      // Capture the intended upload state before the async setState flushes,
      // so allResolved can be computed synchronously against it.
      const newUploadState = { ...uploadState, [key]: 'done' };
      setUploadState(newUploadState);
      const updatedRows = await loadRequests();
      // Check if all items are resolved: server-received OR just-uploaded this session.
      // We use client truth here because the CF trigger runs async after finalize.
      if (updatedRows) {
        const updatedFlat  = flattenAndSort(updatedRows);
        const allResolved  = updatedFlat.length > 0 &&
          updatedFlat.every((e) => e.isReceived || newUploadState[e.key] === 'done');
        if (allResolved) onAllResolved?.();
      }
    } catch (err) {
      console.error('[NotYetInbox] Upload failed:', err);
      setUploadState((s) => ({ ...s, [key]: 'error' }));
      setUploadError((s) => ({
        ...s,
        [key]: err.message || 'Upload failed. Please try again.',
      }));
    }
  };

  if (loading) {
    return (
      <div className="mb-4">
        <SectionHeader />
        <div className="flex items-center gap-2 text-slate-500 text-sm py-3">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-4">
        <SectionHeader />
        <div className="p-3 bg-red-500/10 border border-red-500/25 rounded-lg flex items-start gap-2.5">
          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-red-300">{error}</p>
            <button onClick={loadRequests} className="text-xs text-red-400 hover:text-red-300 underline mt-1">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (requests.length === 0) {
    if (!required) return null;
    return (
      <div className="mb-4">
        <SectionHeader />
        <div className="px-3 py-3 rounded-lg border border-slate-700/40 bg-slate-800/30 text-sm text-slate-500">
          No missing items have been requested yet. Check back or contact intake for details.
        </div>
      </div>
    );
  }

  const flat = flattenAndSort(requests);

  return (
    <div className="mb-4">
      <SectionHeader />

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif,.doc,.docx"
        className="hidden"
        onChange={handleFileSelected}
      />

      <div className="space-y-2">
        {flat.map((entry) => {
          const { key, requestId, itemType, requestedAt, receivedAt } = entry;
          const uState     = uploadState[key] ?? 'idle';
          const label      = REWORK_ITEM_TYPE_LABELS[itemType] ?? itemType;
          const isReceived = entry.isReceived || uState === 'done';
          const reqDate    = formatRequestDate(requestedAt);
          const rcvDate    = formatRequestDate(receivedAt);

          return (
            <div
              key={key}
              className={`flex items-center justify-between gap-4 px-3 py-3 rounded-lg border ${
                isReceived
                  ? 'bg-slate-800/30 border-slate-700/30'
                  : 'bg-amber-500/5 border-amber-500/20'
              }`}
            >
              <div className="min-w-0">
                <p className={`text-sm font-medium ${isReceived ? 'text-slate-300' : 'text-white'}`}>
                  {label}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {isReceived
                    ? rcvDate ? `Received ${rcvDate}` : 'Received'
                    : reqDate ? `Requested ${reqDate}` : 'Requested'}
                </p>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                {isReceived ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-medium">
                      Received
                    </span>
                  </>
                ) : uState === 'uploading' ? (
                  <span className="flex items-center gap-1.5 text-xs text-slate-400">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Uploading…
                  </span>
                ) : (
                  <>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 text-xs font-medium">
                      Open
                    </span>
                    <button
                      onClick={() => handleUploadClick(key, requestId, itemType)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 active:bg-amber-600 text-white text-xs font-medium transition-colors"
                    >
                      <Upload className="w-3 h-3" />
                      Upload File
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}

        {flat.map((entry) => {
          const uErr = uploadError[entry.key];
          if (!uErr) return null;
          return (
            <div key={`err-${entry.key}`} className="flex items-start gap-1.5 px-1 text-xs text-red-300">
              <XCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>{uErr}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SectionHeader() {
  return (
    <div className="mb-3">
      <p className="text-sm font-semibold text-white">Missing Items Requested</p>
      <p className="text-xs text-slate-500 mt-0.5">
        Intake requested the following items to move this referral forward.
      </p>
    </div>
  );
}

// ── Seen/unseen tracking via localStorage ────────────────────────────────────

const SEEN_STORAGE_KEY_PREFIX = 'notyet-seen-';

function getSeenStorageKey(userId) {
  return `${SEEN_STORAGE_KEY_PREFIX}${userId}`;
}

function loadSeenIds(userId) {
  if (!userId) return new Set();
  try {
    const raw = localStorage.getItem(getSeenStorageKey(userId));
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function persistSeenIds(userId, seenSet) {
  if (!userId) return;
  try {
    // Keep only the most recent 200 entries to avoid unbounded growth
    const arr = [...seenSet].slice(-200);
    localStorage.setItem(getSeenStorageKey(userId), JSON.stringify(arr));
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

// ── Main View ─────────────────────────────────────────────────────────────────

const NotYetInboxView = ({ onNavigate, activeFocusTarget, onLearningContext, autoOpenReferralId }) => {
  const { user } = useAuth();
  const userId = user?.uid || user?.id;

  // Merged rows from both queries. Each row has a _inboxType discriminator.
  const [rows, setRows]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError]             = useState(null);
  const [selectedReferral, setSelectedReferral] = useState(null);

  // Pagination state for not_yet_outcome query only.
  // needs_info_ops rows are loaded fully (up to 20) and do not paginate separately.
  const [notYetLastDoc, setNotYetLastDoc] = useState(null);
  const [notYetHasMore, setNotYetHasMore] = useState(false);
  const [totalLoaded, setTotalLoaded]     = useState(0);

  // Success banner: true while the "all items received" confirmation is showing.
  // Shown for 3.5 s after all rework items resolve, then clears selection.
  const [resolvedBanner, setResolvedBanner] = useState(false);

  // Pre-fetched rework request labels for NEEDS_INFO snippets: { [referralId]: string[] }
  const [snippetReworkMap, setSnippetReworkMap] = useState({});

  // Seen/unseen tracking — persisted in localStorage per user
  const [seenIds, setSeenIds] = useState(() => loadSeenIds(userId));

  // Hover state for snippet checklist expansion
  const [hoveredRefId, setHoveredRefId] = useState(null);

  // Mark a referral as seen when selected
  const markAsSeen = useCallback((refId) => {
    if (!refId || !userId) return;
    setSeenIds(prev => {
      if (prev.has(refId)) return prev;
      const next = new Set(prev);
      next.add(refId);
      persistSeenIds(userId, next);
      return next;
    });
  }, [userId]);

  // Wrap setSelectedReferral to also mark as seen
  const selectReferral = useCallback((ref) => {
    setSelectedReferral(ref);
    if (ref) {
      const refId = ref.referralId || ref.id;
      markAsSeen(refId);
    }
  }, [markAsSeen]);

  // Deep-link: auto-open the exact referral when arriving from "Open Referral"
  // (Referral Pings). Fires once, after rows finish loading. Shows a clear notice
  // if the referral isn't present in this inbox.
  const [deepLinkNotice, setDeepLinkNotice] = useState('');
  const autoOpenFired = useRef(false);
  useEffect(() => {
    if (!autoOpenReferralId || autoOpenFired.current || loading) return;
    autoOpenFired.current = true;
    const res = findReferralInBuckets(
      [{ tab: 'not-yet', items: rows, getId: (r) => r.referralId || r.id }],
      autoOpenReferralId,
    );
    if (res.found) {
      selectReferral(res.item);
    } else {
      // Not in the Needs-Info inbox — read-only fallback (non-PHI core) for a
      // specific reason instead of a vague "not available".
      getReferral(user?.tenantId, autoOpenReferralId)
        .then((ref) => setDeepLinkNotice(classifyDeepLinkFailure({ referral: ref })))
        .catch((err) => setDeepLinkNotice(classifyDeepLinkFailure({ error: err })));
    }
  }, [autoOpenReferralId, loading, rows, selectReferral, user?.tenantId]);

  useEffect(() => {
    if (onLearningContext) {
      const refId = selectedReferral ? (selectedReferral.referralId || selectedReferral.id) : null;
      onLearningContext({ selectedReferralId: refId });
    }
    return () => { if (onLearningContext) onLearningContext({}); };
  }, [selectedReferral, onLearningContext]);

  // Guided learning: scroll the active focus target into view when it changes
  // (e.g. the "Complete This Referral" button in the detail panel). The small
  // delay lets the detail panel render after a referral is selected. Missing
  // targets are a safe no-op.
  useEffect(() => {
    if (!activeFocusTarget || typeof document === 'undefined') return;
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-learning-target="${activeFocusTarget}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }, 250);
    return () => clearTimeout(timer);
  }, [activeFocusTarget, selectedReferral]);

  const mergeAndSort = (notYetRows, needsInfoRows) => {
    const tagged = [
      ...notYetRows.map((r)   => ({ ...r, _inboxType: INBOX_TYPE.NOT_YET_OUTCOME })),
      ...needsInfoRows.map((r) => ({ ...r, _inboxType: INBOX_TYPE.NEEDS_INFO_OPS  })),
    ];
    // Urgent/red-flag first, then oldest-first (most-stuck at top).
    tagged.sort(compareInboxRows);
    return tagged;
  };

  const loadReferrals = useCallback(async () => {
    if (!user?.tenantId) return;
    setLoading(true);
    setError(null);
    setNotYetLastDoc(null);
    setNotYetHasMore(false);
    try {
      const [notYetResult, needsInfoResult] = await Promise.all([
        getNotYetReferrals({
          tenantId:     user.tenantId,
          userId:       user.uid || user.id,
          role:         user.role,
          specialtyIds: user.specialtyIds || [],
          pageSize:     PAGE_SIZE,
          lastDoc:      null,
        }),
        getNeedsInfoReferrals({
          tenantId: user.tenantId,
          userId:   user.uid || user.id,
          role:     user.role,
          pageSize: 20,
          lastDoc:  null,
        }),
      ]);

      const merged = mergeAndSort(notYetResult.rows, needsInfoResult.rows);
      setRows(merged);
      setNotYetLastDoc(notYetResult.lastDoc);
      setNotYetHasMore(notYetResult.hasMore);
      setTotalLoaded(merged.length);
    } catch (err) {
      console.error('[NotYetInbox] Error loading referrals:', err);
      setError(safeUserError(err));
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Load more only pages through not_yet_outcome items — needs_info_ops are fully loaded upfront.
  const loadMoreReferrals = useCallback(async () => {
    if (!user?.tenantId || !notYetLastDoc || loadingMore || !notYetHasMore) return;
    setLoadingMore(true);
    try {
      const result = await getNotYetReferrals({
        tenantId:     user.tenantId,
        userId:       user.uid || user.id,
        role:         user.role,
        specialtyIds: user.specialtyIds || [],
        pageSize:     PAGE_SIZE,
        lastDoc:      notYetLastDoc,
      });
      const moreTagged = result.rows.map((r) => ({ ...r, _inboxType: INBOX_TYPE.NOT_YET_OUTCOME }));
      // Re-sort the full list so global ordering (urgent-first, oldest-first) holds across pages.
      setRows((prev) => [...prev, ...moreTagged].sort(compareInboxRows));
      setNotYetLastDoc(result.lastDoc);
      setNotYetHasMore(result.hasMore);
      setTotalLoaded((prev) => prev + moreTagged.length);
    } catch (err) {
      console.error('[NotYetInbox] Error loading more referrals:', err);
      setError(safeUserError(err));
    } finally {
      setLoadingMore(false);
    }
  }, [user, notYetLastDoc, loadingMore, notYetHasMore]);

  useEffect(() => { loadReferrals(); }, [loadReferrals]);

  // Pre-fetch rework request labels for NEEDS_INFO_OPS items so snippets can show a checklist.
  useEffect(() => {
    if (!user?.tenantId || rows.length === 0) return;
    const opsRows = rows.filter((r) => r._inboxType === INBOX_TYPE.NEEDS_INFO_OPS);
    if (opsRows.length === 0) return;
    let cancelled = false;
    (async () => {
      const newMap = {};
      await Promise.all(
        opsRows.map(async (r) => {
          const refId = r.referralId || r.id;
          try {
            const reqs = await listReworkRequests(user.tenantId, refId);
            const openLabels = [];
            for (const req of reqs) {
              for (const entry of normalizeRequest(req)) {
                if (!entry.isReceived) {
                  openLabels.push(REWORK_ITEM_TYPE_LABELS[entry.itemType] ?? entry.itemType);
                }
              }
            }
            newMap[refId] = openLabels;
          } catch {
            newMap[refId] = [];
          }
        }),
      );
      if (!cancelled) setSnippetReworkMap((prev) => ({ ...prev, ...newMap }));
    })();
    return () => { cancelled = true; };
  }, [rows, user?.tenantId]);

  // Called by RequestedMissingItemsSection when GP uploads the final requested item.
  // Removes the referral from the list immediately (client truth) and shows a
  // 3.5-second success banner before clearing the selection.
  const handleAllResolved = useCallback(() => {
    if (!selectedReferral) return;
    const resolvedId = selectedReferral.referralId || selectedReferral.id;
    setRows((prev) => prev.filter((r) => (r.referralId || r.id) !== resolvedId));
    setTotalLoaded((prev) => Math.max(0, prev - 1));
    setResolvedBanner(true);
    setTimeout(() => {
      setResolvedBanner(false);
      selectReferral(null);
    }, 3500);
  }, [selectedReferral, selectReferral]);

  // Gate rerun — only for not_yet_outcome items.
  const handleCompleteReferral = (referral) => {
    if (!referral || referral._inboxType !== INBOX_TYPE.NOT_YET_OUTCOME) return;
    const resolvedReferralId = referral.referralId || referral.id;
    if (!resolvedReferralId) {
      console.error('[NotYetInbox] Cannot complete referral — referralId is missing:', referral);
      return;
    }
    const resumeData = {
      referralId:          resolvedReferralId,
      packId:              referral.packId,
      specialtyId:         referral.specialtyId,
      versionId:           referral.versionId,
      answers:             redactBlockingPhiDeep(referral.answers || {}),
      localId:             referral.localId,
      telehealthContext:   referral.telehealthContext,
      triggeredRedFlagIds: referral.triggeredRedFlagIds || [],
      age:                 referral.age || '',
      sex:                 referral.sex || '',
      visitType:           referral.visitType || '',
      visitPurpose:        referral.visitPurpose || '',
      reasonForEscalation:
        referral.reasonForEscalation ||
        referral.telehealthContext?.reasonForEscalation ||
        '',
      missingFieldIds: referral.missingFieldIds || [],
    };
    sessionStorage.setItem('resumeReferral', JSON.stringify(resumeData));
    if (onNavigate) onNavigate('gate');
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return (
        date.toLocaleDateString() +
        ' ' +
        date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      );
    } catch {
      return 'N/A';
    }
  };

  // ── Loading ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-full">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-amber-400 mx-auto mb-4" />
          <p className="text-slate-400">Loading referrals…</p>
        </div>
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────

  if (error && rows.length === 0) {
    return (
      <div className="p-8">
        <div className="p-8 bg-red-500/10 border border-red-500/30 rounded-2xl text-center max-w-2xl mx-auto">
          <div className="w-16 h-16 rounded-2xl bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h3 className="text-xl font-semibold text-red-400 mb-2">Unable to Load Referrals</h3>
          <p className="text-slate-400 mb-6">{error}</p>
          <button
            onClick={loadReferrals}
            className="flex items-center gap-2 px-6 py-3 bg-red-500 hover:bg-red-600 rounded-xl text-white font-medium mx-auto"
          >
            <RefreshCw className="w-5 h-5" /> Retry
          </button>
        </div>
      </div>
    );
  }

  // ── Empty ──────────────────────────────────────────────────────────────────

  if (rows.length === 0) {
    return (
      <div className="p-8">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-white mb-2">Not Yet Inbox</h1>
          <p className="text-slate-400">Referrals requiring your action</p>
        </div>
        <div className="p-12 bg-slate-800/30 border border-slate-700/50 rounded-2xl text-center max-w-2xl mx-auto">
          <div className="w-20 h-20 rounded-2xl bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-400" />
          </div>
          <h2 className="text-2xl font-semibold text-white mb-2">All Clear!</h2>
          <p className="text-slate-400">No referrals require your attention right now.</p>
        </div>
      </div>
    );
  }

  // ── List + Detail ──────────────────────────────────────────────────────────

  const isNeedsInfoOps = selectedReferral?._inboxType === INBOX_TYPE.NEEDS_INFO_OPS;
  const unseenCount = rows.filter(r => !seenIds.has(r.referralId || r.id)).length;

  return (
    <div className="p-4 md:p-8">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-white mb-2">Not Yet Inbox</h1>
            <p className="text-slate-400">
              {totalLoaded} referral{totalLoaded !== 1 ? 's' : ''} requiring your action
              {unseenCount > 0 && (
                <span className="ml-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-xs font-semibold">
                  {unseenCount} new
                </span>
              )}
            </p>
          </div>
          <button
            onClick={loadReferrals}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 rounded-lg text-white text-sm"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {deepLinkNotice && (
        <div className="mb-4 flex items-start gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
          <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
          <p className="flex-1 text-sm text-amber-300">{deepLinkNotice}</p>
          <button onClick={() => setDeepLinkNotice('')} className="text-xs text-amber-400/80 hover:text-amber-300">Dismiss</button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">

        {/* ── Referral list ── */}
        <div
          data-learning-target="not_yet_referral_list"
          className={`bg-slate-800/30 border rounded-2xl p-4 flex flex-col transition-all duration-300 ${
            activeFocusTarget === 'not_yet_referral_list'
              ? 'border-amber-400/60 ring-2 ring-amber-400/20'
              : 'border-slate-700/50'
          }`}
          style={{ maxHeight: '70vh' }}
        >
          <div className="mb-4 flex-shrink-0">
            <h3 className="text-lg font-semibold text-white">Referrals</h3>
          </div>
          <div className="flex-1 overflow-y-auto space-y-3 pr-2 min-h-0">
            {rows.map((ref, rowIndex) => {
              const refId           = ref.referralId || ref.id;
              const isOps           = ref._inboxType === INBOX_TYPE.NEEDS_INFO_OPS;
              const isSelected      = selectedReferral?.referralId === refId ||
                                      selectedReferral?.id === refId;
              const isUnseen        = !seenIds.has(refId);

              // ── Build checklist items for this snippet ──
              const checklistItems = isOps
                ? (snippetReworkMap[refId] || [])
                : (ref.missingFieldIds || []).map(formatFieldIdLabel);
              const visibleItems = checklistItems.slice(0, SNIPPET_CHECKLIST_MAX);
              const overflowCount = Math.max(0, checklistItems.length - SNIPPET_CHECKLIST_MAX);

              // ── Request date: when the action was created, not when referral was entered ──
              const requestDate = isOps
                ? (ref.workflowStatusEnteredAt || ref.savedAt)
                : (ref.createdAt || ref.savedAt);

              const isHovered = hoveredRefId === refId;
              const showFullList = isHovered && checklistItems.length > SNIPPET_CHECKLIST_MAX;
              const displayItems = showFullList ? checklistItems : visibleItems;

              return (
                <div
                  key={refId}
                  {...(rowIndex === 0 ? { 'data-learning-target': 'not_yet_first_card' } : {})}
                  onMouseEnter={() => setHoveredRefId(refId)}
                  onMouseLeave={() => setHoveredRefId(null)}
                  className={activeFocusTarget === 'not_yet_first_card' && rowIndex === 0 ? 'learning-focus-highlight-subtle rounded-xl transition-all duration-300' : ''}
                >
                  <button
                    onClick={() => selectReferral(ref)}
                    className={`w-full p-4 rounded-xl border text-left transition-all relative ${
                      isSelected
                        ? isOps
                          ? 'bg-blue-500/20 border-blue-500/50'
                          : 'bg-amber-500/20 border-amber-500/50'
                        : isUnseen
                          ? 'bg-slate-800/60 border-l-2 border-l-amber-400 border-t border-r border-b border-slate-700/50 hover:border-slate-600'
                          : 'bg-slate-800/40 border-slate-700/50 hover:border-slate-600'
                    }`}
                  >
                    {/* Top row: badge + ID */}
                    <div className="flex items-center gap-2 mb-2">
                      {isUnseen && !isSelected && (
                        <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-50" />
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-400" />
                        </span>
                      )}
                      {isOps ? (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-300 text-xs font-medium flex-shrink-0">
                          <Inbox className="w-3 h-3" />
                          Docs Requested
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 text-xs font-medium flex-shrink-0">
                          <Clock className="w-3 h-3" />
                          Not Yet
                        </span>
                      )}
                      {/* Docs Requested is the operational recovery state; once docs
                          are requested, workflow.status is always NEEDS_INFO, which
                          conveys nothing about clinical intent. Pull the real Gate
                          outcome (routine / urgent / not_yet) so the user sees why
                          this referral is in the inbox. Engine Not Yet cards skip
                          this — the primary badge already says Not Yet. */}
                      {isOps && (ref.outcome?.status || ref.outcomeStatus) && (
                        <RGStatusBadge
                          type="outcome"
                          status={ref.outcome?.status ?? ref.outcomeStatus}
                        />
                      )}
                      <span className="text-sm font-mono text-white truncate">
                        {refId}
                      </span>
                      <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0 ml-auto" />
                    </div>

                    {/* Two-column body: left = info, right = missing items */}
                    <div className="flex gap-3">
                      {/* Left column — pack + date */}
                      <div className="flex-1 min-w-0 space-y-1 text-xs text-slate-400">
                        <div className="flex items-center gap-2">
                          <Package className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{ref.packId || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-3 h-3 flex-shrink-0" />
                          <span>Requested {formatRequestDate(requestDate) || 'N/A'}</span>
                        </div>
                      </div>

                      {/* Right column — missing items checklist */}
                      {checklistItems.length > 0 && (
                        <div className="flex-1 min-w-0 border-l border-slate-700/40 pl-3">
                          <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                            Missing Items{showFullList ? ` (${checklistItems.length})` : ''}
                          </p>
                          <ul className="space-y-0.5">
                            {displayItems.map((label, i) => (
                              <li key={i} className="flex items-center gap-1.5 text-xs text-slate-300">
                                <CircleDot className="w-2.5 h-2.5 text-amber-400 flex-shrink-0" />
                                <span className="truncate">{label}</span>
                              </li>
                            ))}
                          </ul>
                          {!showFullList && overflowCount > 0 && (
                            <p className="text-[10px] text-slate-500 mt-0.5 pl-4">
                              +{overflowCount} more
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </button>

                </div>
              );
            })}

            {notYetHasMore && (
              <button
                onClick={loadMoreReferrals}
                disabled={loadingMore}
                className="w-full p-4 rounded-xl border border-dashed border-slate-600 hover:border-slate-500 text-slate-400 hover:text-white transition-all flex items-center justify-center gap-2"
              >
                {loadingMore ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Loading more…</>
                ) : (
                  <><ChevronDown className="w-4 h-4" /> Load More</>
                )}
              </button>
            )}
          </div>
        </div>

        {/* ── Referral detail ── */}
        <div
          data-learning-target="not_yet_detail_panel"
          className={`bg-slate-800/30 border rounded-2xl p-6 transition-all duration-300 ${
            activeFocusTarget === 'not_yet_detail_panel'
              ? 'border-amber-400/60 ring-2 ring-amber-400/20'
              : 'border-slate-700/50'
          }`}
        >
          {resolvedBanner ? (
            /* Success state — shown for 3.5 s after final item uploaded */
            <div className="py-16 text-center">
              <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">All Items Received</h3>
              <p className="text-sm text-slate-400 max-w-xs mx-auto">
                All requested items have been received. Referral moved forward — intake will schedule your appointment.
              </p>
            </div>
          ) : selectedReferral ? (
            <div>
              {/* Header */}
              <div className="flex items-center gap-3 mb-5">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  isNeedsInfoOps ? 'bg-blue-500/20' : 'bg-amber-500/20'
                }`}>
                  {isNeedsInfoOps
                    ? <Inbox className="w-6 h-6 text-blue-400" />
                    : <FileText className="w-6 h-6 text-amber-400" />
                  }
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold text-white">Referral Details</h3>
                  <p className="text-sm text-slate-400 font-mono truncate">
                    {selectedReferral.referralId || selectedReferral.id}
                  </p>
                </div>
              </div>

              {/* Status + outcome badges */}
              <div className="flex flex-wrap gap-2 mb-5">
                {isNeedsInfoOps ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-500/15 text-blue-300 text-xs font-medium">
                    <Inbox className="w-3 h-3" />
                    Documents Requested
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-300 text-xs font-medium">
                    <Clock className="w-3 h-3" />
                    Not Yet
                  </span>
                )}
                <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-slate-700/60 text-slate-300 text-xs font-medium">
                  {selectedReferral.specialtyId || 'N/A'}
                </span>
                <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-slate-700/60 text-slate-400 text-xs">
                  {selectedReferral.packId || 'N/A'} v{selectedReferral.versionId || '1.0.0'}
                </span>
                {isNeedsInfoOps && (
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                    selectedReferral.outcome?.status === 'urgent'
                      ? 'bg-red-500/15 text-red-300'
                      : 'bg-emerald-500/15 text-emerald-300'
                  }`}>
                    {selectedReferral.outcome?.status === 'urgent' ? 'Urgent' : 'Routine'}
                  </span>
                )}
              </div>

              {/* NOT_YET_OUTCOME only: engine reason + missing clinical fields */}
              {!isNeedsInfoOps && selectedReferral.outcome?.reason && (
                <div className="p-3 bg-amber-500/8 border border-amber-500/20 rounded-lg mb-4">
                  <p className="text-xs text-amber-400 mb-1 font-medium">Gate Reason</p>
                  <p className="text-sm text-slate-200">{selectedReferral.outcome.reason}</p>
                </div>
              )}

              {!isNeedsInfoOps && selectedReferral.missingFieldIds?.length > 0 && (
                <div className="p-3 bg-slate-800/50 rounded-lg mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                    <p className="text-xs font-medium text-amber-400">Missing Clinical Requirements</p>
                  </div>
                  <ul className="space-y-1.5">
                    {selectedReferral.missingFieldIds.map((fieldId, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-xs text-slate-300">
                        <span className="w-4 h-4 rounded flex items-center justify-center bg-slate-700 text-slate-400 flex-shrink-0 text-[10px]">
                          {idx + 1}
                        </span>
                        <span>{fieldId}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* NEEDS_INFO_OPS only: contextual explanation */}
              {isNeedsInfoOps && (
                <div className="p-3 bg-blue-500/8 border border-blue-500/20 rounded-lg mb-4">
                  <p className="text-xs text-blue-400 mb-1 font-medium">Action Required</p>
                  <p className="text-sm text-slate-300">
                    Intake has requested additional documents to proceed with this referral.
                    Upload each item below to move it forward.
                  </p>
                </div>
              )}

              {/* Missing Items — always visible for needs_info_ops; optional for not_yet_outcome */}
              <div className="border-t border-slate-700/50 pt-4 mb-4">
                <RequestedMissingItemsSection
                  key={selectedReferral.referralId || selectedReferral.id}
                  tenantId={user.tenantId}
                  referralId={selectedReferral.referralId || selectedReferral.id}
                  required={isNeedsInfoOps}
                  onAllResolved={handleAllResolved}
                />
              </div>

              {/* Submitted / reference */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Submitted</p>
                  <p className="text-xs text-white">
                    {formatDate(selectedReferral.createdAt || selectedReferral.savedAt)}
                  </p>
                </div>
                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Reference</p>
                  <p className="text-xs text-white font-mono truncate">
                    {selectedReferral.referralId || selectedReferral.id || 'N/A'}
                  </p>
                </div>
              </div>

              {/* Messaging card */}
              <div className="border-t border-slate-700/50 pt-4 mb-4">
                <MessagingCard
                  tenantId={user.tenantId}
                  referralId={selectedReferral.referralId || selectedReferral.id}
                  specialtyName={selectedReferral.specialtyId || 'Specialty'}
                  packName={selectedReferral.packId || ''}
                  missingItemTypeLabels={selectedReferral.missingFieldIds || []}
                  notYetReasons={
                    selectedReferral.outcome?.reason
                      ? [selectedReferral.outcome.reason]
                      : []
                  }
                  receiptToken={selectedReferral.receiptToken || null}
                  submittedAt={
                    selectedReferral.createdAt?.toDate
                      ? selectedReferral.createdAt.toDate().toISOString()
                      : selectedReferral.createdAt || null
                  }
                  actorUid={user.uid || user.id}
                  actorRole={user.role || 'gp_employee'}
                  canLogTouch={true}
                />
              </div>

              {/* Action — conditionally rendered based on inbox type */}
              <div className="border-t border-slate-700/50 pt-4">
                {isNeedsInfoOps ? (
                  // NEEDS_INFO_OPS: upload documents only — no Gate rerun.
                  <div className="p-3 bg-blue-500/8 border border-blue-500/15 rounded-xl text-center">
                    <p className="text-sm text-blue-300 font-medium mb-1">Upload each requested item above</p>
                    <p className="text-xs text-slate-500">
                      Once intake receives all items, this referral will advance automatically.
                    </p>
                  </div>
                ) : (
                  // NOT_YET_OUTCOME: Gate rerun to complete clinical fields.
                  <>
                    <button
                      data-learning-target="not_yet_complete_referral_button"
                      onClick={() => handleCompleteReferral(selectedReferral)}
                      className={`w-full flex items-center justify-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 rounded-xl text-white font-medium transition-all duration-300 ${
                        activeFocusTarget === 'not_yet_complete_referral_button'
                          ? 'ring-2 ring-amber-400/40 ring-offset-2 ring-offset-slate-900'
                          : ''
                      }`}
                    >
                      <Edit3 className="w-5 h-5" />
                      Complete This Referral
                    </button>
                    <p className="text-xs text-slate-500 text-center mt-2">
                      Fill in missing fields and submit
                    </p>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="py-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-slate-700/50 flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-slate-500" />
              </div>
              <p className="text-slate-400">Select a referral to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotYetInboxView;
